import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Route,
  Routes,
  Outlet,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";

import { TourProvider, useTour } from "@reactour/tour";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBack from "@mui/icons-material/ArrowBack";

import { useDispatch, useSelector } from "react-redux";
import {
  validateSession,
  resetSession,
  validateRole,
  setSessionExpiredLogout,
} from "./store/actions/sessionsActions";
import { withSessionCheck } from "./components/hoc/withSessionCheck";
import {
  connectWebsocket,
  disconnectWebsocket,
} from "./store/actions/websocketActions";
import Home from "./components/pages/Home";
import Subscription from "./components/pages/Subscription";
import SigneeLogin from "./components/signee/SigneeLogin";
import Login from "./components/pages/Login";
import AcceptInstant from "./components/notary/AcceptInstant";
import SuccessChangePlan from "./components/SuccessChangePlan";
import SuccessCreateExpiredSubscription from "./components/SuccessCreateExpiredSubscription";
import Register from "./components/pages/Register";
import VerifyEmailPage from "./components/auth/VerifyEmailPage";
import Dashboard from "./components/pages/Dashboard";
import CreateNoc from "./components/pages/CreateNoc";
import Payment from "./components/pages/Payment";
import SelectCounties from "./components/pages/SelectCounties";
import Presets from "./components/pages/Presets";
import Contractors from "./components/pages/Contractors";
import Branches from "./components/pages/Branches";
import ProfileSettings from "./components/pages/ProfileSettings";
import Overview from "./components/pages/admin/Overview";
import Management from "./components/pages/admin/Management";
import Licenses from "./components/pages/admin/Licenses";
import Tasks from "./components/pages/admin/Tasks";
import Reports from "./components/pages/admin/Reports";
import SessionUsage from "./components/pages/admin/SessionUsage";
import FormsManagement from "./components/pages/admin/FormsManagement";
import ResetPassword from "./components/pages/ResetPassword";
import Customers from "./components/pages/admin/Customers";
import Sales from "./components/pages/admin/Sales";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DynamicAuth from "./components/pages/DynamicAuth";
import SubuserFinalize from "./components/pages/SubuserFinalize";
import "./styles/globals.css";
import TotpAuth from "./components/pages/TotpAuth";
import EmailFaAuth from "./components/pages/EmailFaAuth";
import EmailFaAuthPass from "./components/pages/EmailFaAuthPass";
import SmsFaAuth from "./components/pages/SmsFaAuth";

import NotaryRegister from "./components/notary/NotaryRegister";
import RegisterSignee from "./components/notary/signee/RegisterSignee";
import RegisterRecordSignee from "./components/notary/signee/RegisterRecordSignee";

import ManageSigneeAppointment from "./components/signee/ManageSigneeAppointment";

// SALES
import SalesRegister from "./components/pages/sales/SalesRegister";
import SalesDashboard from "./components/pages/sales/SalesDashboard";
import SalesRevenue from "./components/pages/sales/SalesRevenue";
import SalesCustomers from "./components/pages/sales/SalesCustomers";
import SalesAuthWrapper from "./components/hoc/SalesAuthWrapper";
import { SalesLayout } from "./components/layout/sales/SalesLayout";
import SalesLogin from "./components/pages/sales/SalesLogin";
import CompleteUserRegistration from "./components/pages/CompleteUserRegistration";
// import EmbeddedSigningComponent from "./components/securedSigning/EmbeddedSigningComponent";

import TotpResetAuth from "./components/pages/TotpResetAuth";
import EmailResetFaAuth from "./components/pages/EmailResetFaAuth";
import EmailFaResetAuthPass from "./components/pages/EmailFaResetAuthPass";
import SmsFaResetAuth from "./components/pages/SmsFaResetAuth";

import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { IconButton, useMediaQuery } from "@mui/material";
import Subusers from "./components/pages/Subusers";
import Notaries from "./components/pages/admin/Notaries";
import NotaryDashboard from "./components/notary/NotaryDashboard";
import NotaryOrganization from "./components/notary/NotaryOrganization";
import NotaryJobManagement from "./components/notary/NotaryJobManagement";
import NotaryIpenManagement from "./components/notary/NotaryIpenManagement";
import FinishNotary from "./components/notary/FinishNotary";
import NotarySettings from "./components/notary/NotarySettings";
import MyPermits from "./components/pages/permits/MyPermits";
import SubuserPermits from "./components/pages/permits/SubuserPermits";
import CustomersPackages from "./components/pages/permits/CustomersPackages";
import NotarizationRequests from "./components/pages/permits/NotarizationRequests";
import NotFound from "./components/pages/NotFound";
import ShortRedirect from "./components/pages/ShortRedirect";
import { setCurrentSession } from "./store/actions/sessionsActions";

import {
  checkRoleValidationNeeded,
  getSessionNameByPath,
  isAllowedSubAdminRoute,
  isPathProtected,
} from "./functions/helperFunctions";
import { isPermitRockstarDomain } from "./utils/domainUtils";
import BuildingPermit from "./components/pages/BuildingPermit";
import UserRonCompleted from "./components/pages/ron/UserRonCompleted";
import NotaryRonCompleted from "./components/pages/ron/NotaryRonCompleted";
import { subUserTourSteps, tourSteps } from "./data/walkthroughDemo/steps";
import { LoadingButton } from "@mui/lab";
import CustomDocumentsPrepareSign from "./components/pages/custom-documents/CustomDocumentsPrepareSign";
import CustomDocumentsPrepareNoc from "./components/pages/custom-documents/CustomDocumentsPrepareNoc";
import CustomDocumentsPreparedDocs from "./components/pages/custom-documents/CustomDocumentsPreparedDocs";
import PrivateProviderContractors from "./components/pages/privateProvider/PrivateProviderContractors";
import PpShell from "./components/pages/privateProvider/layouts/PpShell";
import PpDashboardPage from "./components/pages/privateProvider/Dashboard/PpDashboardPage";
import PpSubmissionsListPage from "./components/pages/privateProvider/Submissions/PpSubmissionsListPage";
import PpNewSubmissionPage from "./components/pages/privateProvider/Submissions/PpNewSubmissionPage";
import PpReviewWorkbenchPage from "./components/pages/privateProvider/Submissions/PpReviewWorkbenchPage";
import PpSubmissionDetailsPage from "./components/pages/privateProvider/Submissions/PpSubmissionDetailsPage";
import PpFinanceDashboardPage from "./components/pages/privateProvider/Finance/PpFinanceDashboardPage";
import PpPayoutsPage from "./components/pages/privateProvider/Finance/PpPayoutsPage";
import PpStatementsPage from "./components/pages/privateProvider/Finance/PpStatementsPage";
import PpSettingsPage from "./components/pages/privateProvider/Settings/PpSettingsPage";
import PpNotificationsCenterPage from "./components/pages/privateProvider/Notifications/PpNotificationsCenterPage";
import PpInspectionsBoardPage from "./components/pages/privateProvider/Inspections/PpInspectionsBoardPage";
import PpInspectionTemplatesPage from "./components/pages/privateProvider/Inspections/PpInspectionTemplatesPage";
import PpInspectionPermitView from "./components/pages/privateProvider/Inspections/PpInspectionPermitView";
import PpMessagingCenterPage from "./components/pages/privateProvider/Messaging/PpMessagingCenterPage";
import PpMessageThreadPage from "./components/pages/privateProvider/Messaging/PpMessageThreadPage";
import ContractorPermitsPage from "./components/pages/privateProvider/contractor/ContractorPermitsPage";
import ContractorPermitInspectionsPage from "./components/pages/privateProvider/contractor/ContractorPermitInspectionsPage";
import ContractorUploadsPage from "./components/pages/privateProvider/contractor/ContractorUploadsPage";
import ContractorMessagingPage from "./components/pages/privateProvider/contractor/ContractorMessagingPage";
import ContractorShell from "./components/pages/privateProvider/contractor/ContractorShell";
import PpEditInspectionsPage from "./components/pages/privateProvider/Inspections/PpEditInspectionsPage";
import PpStampingWorkflowPage from "./components/pages/privateProvider/Submissions/PpStampingWorkflowPage";
import PpDocumentPreviewPage from "./components/pages/privateProvider/Submissions/PpDocumentPreviewPage";
import PpCityReportingPage from "./components/pages/privateProvider/Settings/PpCityReportingPage";
import PpDigitalSignatureSetupPage from "./components/pages/privateProvider/Settings/PpDigitalSignatureSetupPage";
// Photo and Drawing Features
import PpPropertyPhotoGalleryPage from "./components/pages/privateProvider/Photos/PpPropertyPhotoGalleryPage";
import PpMobilePhotoCaptureComponent from "./components/pages/privateProvider/Photos/PpMobilePhotoCaptureComponent";
import PpPhotoAnnotationTools from "./components/pages/privateProvider/Photos/PpPhotoAnnotationTools";
import PpPhotoRequirementsSystem from "./components/pages/privateProvider/Photos/PpPhotoRequirementsSystem";

// Analytics
import { loadGtag, trackPageView } from "./analytics/gtag";
import { loadGTM } from "./analytics/gtm";

// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE);
const EMPTY_ARRAY = [];

// Keep this wrapper stable across App re-renders to avoid route element remounts.
const NotaryRouteOutlet = () => <Outlet />;
const NotaryProtectedLayoutComponent = withSessionCheck(NotaryRouteOutlet, [
  "notarySession",
]);

export const StripeContext = createContext({ current: null });

const StripeElementsFromContext = ({ children }) => {
  const stripeRef = useContext(StripeContext);

  return <Elements stripe={stripeRef.current}>{children}</Elements>;
};

const MyPermitsComponent = withSessionCheck(
  MyPermits,
  ["userSession", "subUserSession", "privateProviderSession"],
  StripeElementsFromContext,
  {},
);
const SubuserPermitsComponent = withSessionCheck(
  SubuserPermits,
  ["userSession", "subUserSession", "privateProviderSession"],
  StripeElementsFromContext,
  {},
);
const CustomersPackagesComponent = withSessionCheck(
  CustomersPackages,
  ["userSession"],
  StripeElementsFromContext,
  {},
);
const SubscriptionComponent = withSessionCheck(
  Subscription,
  ["userSession", "subUserSession", "privateProviderSession"],
  StripeElementsFromContext,
  {},
);
const PaymentComponent = withSessionCheck(
  Payment,
  ["userSession", "subUserSession", "privateProviderSession"],
  StripeElementsFromContext,
  {},
);
const DashboardComponent = withSessionCheck(
  Dashboard,
  ["userSession", "subUserSession", "privateProviderSession"],
  StripeElementsFromContext,
  {},
);
const CreateNocComponent = withSessionCheck(
  CreateNoc,
  ["userSession", "subUserSession", "privateProviderSession"],
  StripeElementsFromContext,
  {},
);
const SelectCountiesComponent = withSessionCheck(
  SelectCounties,
  ["userSession", "subUserSession", "privateProviderSession"],
  StripeElementsFromContext,
);
const NotarizationRequestsComponent = withSessionCheck(
  NotarizationRequests,
  ["userSession", "subUserSession", "privateProviderSession"],
  StripeElementsFromContext,
);
const CustomDocumentsPrepareNocComponent = withSessionCheck(
  CustomDocumentsPrepareNoc,
  ["userSession", "subUserSession", "privateProviderSession"],
  StripeElementsFromContext,
);

const App = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { setIsOpen } = useTour();
  // const isReadyToFetch = useSelector((state) => state.nocData?.isReadyToFetch);
  const showTourNav = useSelector((state) => state?.nocData?.showTourNav);
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const sessions = useSelector((state) => state.sessions);
  const currentRole = useSelector((state) => state?.sessions?.currentRole);
  const currentSessionUserEmail = useSelector((state) => {
    const activeSession = state?.sessions?.currentSession;
    return activeSession
      ? state?.[activeSession]?.user?.user?.email
      : undefined;
  });
  const subAdmins = useSelector((state) => state?.nocData?.subAdmins);
  const subAdminDomains = subAdmins?.subAdminDomains ?? EMPTY_ARRAY;
  const subAdminPages = subAdmins?.subAdminPages ?? EMPTY_ARRAY;
  const [stripe, setStripe] = useState(null);
  const stripeRef = useRef(null);
  const isConnecting = useRef(false);

  const sessionSetRef = useRef();
  const validatedRoleRef = useRef();

  // ANALYTICS
  const GA4_MARKETING = process.env.REACT_APP_GA4_MARKETING;
  const ADS_ID = null;
  const DOMAIN = process.env.REACT_APP_DOMAIN;
  const GTM_CONTAINER = process.env.REACT_APP_GTM_CONTAINER;

  useEffect(() => {
    if (DOMAIN === "permitrockstar.app") {
      loadGtag({
        ga4Id: GA4_MARKETING,
        adsId: ADS_ID || null,
        crossDomains: ["permitrockstar.com", "permitrockstar.app"],
        sendPageViewAutomatically: false,
      });
      // Load GTM container (do NOT configure GA4 inside GTM to avoid duplicates)
      if (GTM_CONTAINER) {
        loadGTM(GTM_CONTAINER);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (DOMAIN === "permitrockstar.app") {
      trackPageView(location.pathname + location.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
  //////////////////////////////////////////////////////////////

  useEffect(() => {
    const initStripe = async () => {
      if (isConnecting.current) return;
      isConnecting.current = true;

      try {
        const stripeInstance = await loadStripe(process.env.REACT_APP_STRIPE);

        if (stripeInstance) {
          stripeRef.current = stripeInstance;
          setStripe(stripeInstance);
        } else {
          console.error("Stripe failed to initialize.");
        }
      } catch (err) {
        console.error("Stripe connection error:", err);
      } finally {
        // Wait before allowing next reconnect attempt
        setTimeout(() => {
          isConnecting.current = false;
        }, 10000); // retry max once every 10 seconds
      }
    };

    if (!stripe) {
      initStripe();
    }
  }, [stripe]);

  useEffect(() => {
    const handleValidateRole = async () => {
      if (sessions?.sessionForcedLogout) {
        return;
      }

      const needRoleValidation = checkRoleValidationNeeded(location.pathname);
      if (needRoleValidation && !currentRole) {
        const validatedRole = await dispatch(validateRole());
        if (validatedRole === false && !sessions?.sessionForcedLogout) {
          dispatch(setSessionExpiredLogout());
        }
      }
    };

    if (!validatedRoleRef?.current) {
      validatedRoleRef.current = true;
      handleValidateRole();

      const timeOuts = setTimeout(() => {
        validatedRoleRef.current = false;
      }, 500);

      return () => clearTimeout(timeOuts);
    }
  }, [
    currentRole,
    dispatch,
    location.pathname,
    navigate,
    setIsOpen,
    sessions?.sessionForcedLogout,
  ]);

  useEffect(() => {
    if (sessions?.sessionForcedLogout) {
      return;
    }

    const handler = setTimeout(() => {
      const checkSession = async () => {
        if (sessions?.sessionForcedLogout) {
          return;
        }

        const isAllowedSubAdmin = isAllowedSubAdminRoute({
          email: currentSessionUserEmail,
          subAdminDomains,
          subAdminPages,
          path: location.pathname,
        });

        // Determine session details based on the current path
        const sessionDetails = getSessionNameByPath(
          location.pathname,
          currentRole,
          { bypassRoleCheck: isAllowedSubAdmin },
        );
        //! sessionDetails => MEAN IT'S HIT A PROTECTED ROUTE!!!!
        if (sessionDetails) {
          const { sessionName, requiredCookie, needSocket } = sessionDetails;
          const sessionToken = sessions?.[sessionName]?.token;
          const validated = sessions?.[sessionName]?.isValidated;
          const wsConnected = sessions?.[sessionName]?.wsConnected;

          if (
            !sessions?.currentSession &&
            validated &&
            sessionToken &&
            wsConnected
          ) {
            if (!sessionSetRef?.current) {
              sessionSetRef.current = true;
              dispatch(setCurrentSession(sessionName));
            }
          } else if (!sessionToken || !validated) {
            const validated = await dispatch(
              validateSession(sessionName, requiredCookie, needSocket),
            );
            if (validated === false && !sessions?.sessionForcedLogout) {
              dispatch(setSessionExpiredLogout());
              return;
            }
          } else if (sessionToken && validated && !wsConnected) {
            if (sessionName === "notarySession") {
              // Give the browser a brief moment to persist the fresh notary cookie
              // before opening the WebSocket handshake.
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
            dispatch(connectWebsocket(sessionName, sessionToken));
          }
        } else {
          const pathIsProtected = isPathProtected(location.pathname);

          if (pathIsProtected) {
            if (
              sessions?.currentRole &&
              sessions?.currentRole?.length > 0 &&
              !isAllowedSubAdmin
            ) {
              navigate("/dashboard", { replace: true });
            }
          } else if (sessions?.currentSession) {
            dispatch(disconnectWebsocket(sessions?.currentSession));
            dispatch(resetSession(sessions?.currentSession));
          }
        }
      };

      checkSession();
    }, 1200);

    return () => {
      clearTimeout(handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.pathname,
    sessions,
    currentRole,
    dispatch,
    navigate,
    currentSessionUserEmail,
    subAdminDomains,
    subAdminPages,
  ]);

  useEffect(() => {
    if (!isPathProtected(location.pathname)) {
      sessionSetRef.current = false;
    }
  }, [location.pathname]);

  const BuildingPermitComponent = withSessionCheck(BuildingPermit, [
    "userSession",
    "subUserSession",
    "privateProviderSession",
  ]);
  const PresetsComponent = withSessionCheck(Presets, [
    "userSession",
    "subUserSession",
    "privateProviderSession",
  ]);

  const CustomDocumentsPrepareSignComponent = withSessionCheck(
    CustomDocumentsPrepareSign,
    ["userSession", "subUserSession", "privateProviderSession"],
  );
  const CustomDocumentsPreparedDocsComponent = withSessionCheck(
    CustomDocumentsPreparedDocs,
    ["userSession", "subUserSession", "privateProviderSession"],
  );
  const ProfileSettingsComponent = withSessionCheck(ProfileSettings, [
    "userSession",
    "subUserSession",
    "privateProviderSession",
  ]);

  const ContractorsComponent = withSessionCheck(Contractors, ["userSession"]);
  const BranchesComponent = withSessionCheck(Branches, ["userSession"]);

  const PrivateProviderContractorsComponent = withSessionCheck(
    PrivateProviderContractors,
    ["privateProviderSession"],
  );

  // Private Provider Module Components
  const PpShellComponent = withSessionCheck(PpShell, ["userSession"]);
  const PpDashboardComponent = withSessionCheck(PpDashboardPage, [
    "userSession",
  ]);
  const PpSubmissionsListComponent = withSessionCheck(PpSubmissionsListPage, [
    "userSession",
  ]);
  const PpNewSubmissionComponent = withSessionCheck(PpNewSubmissionPage, [
    "userSession",
  ]);
  const PpReviewWorkbenchComponent = withSessionCheck(PpReviewWorkbenchPage, [
    "userSession",
  ]);
  const PpSubmissionDetailsComponent = withSessionCheck(
    PpSubmissionDetailsPage,
    ["userSession"],
  );
  const PpFinanceDashboardComponent = withSessionCheck(PpFinanceDashboardPage, [
    "userSession",
  ]);
  const PpPayoutsComponent = withSessionCheck(PpPayoutsPage, ["userSession"]);
  const PpStatementsComponent = withSessionCheck(PpStatementsPage, [
    "userSession",
  ]);
  const PpSettingsComponent = withSessionCheck(PpSettingsPage, ["userSession"]);
  const PpDigitalSignatureSetupComponent = withSessionCheck(
    PpDigitalSignatureSetupPage,
    ["userSession"],
  );
  const PpNotificationsCenterComponent = withSessionCheck(
    PpNotificationsCenterPage,
    ["userSession"],
  );
  const PpInspectionsBoardComponent = withSessionCheck(PpInspectionsBoardPage, [
    "userSession",
  ]);
  const PpInspectionTemplatesComponent = withSessionCheck(
    PpInspectionTemplatesPage,
    ["userSession"],
  );
  const PpInspectionPermitViewComponent = withSessionCheck(
    PpInspectionPermitView,
    ["userSession"],
  );
  const PpMessagingCenterComponent = withSessionCheck(PpMessagingCenterPage, [
    "userSession",
  ]);
  const PpMessageThreadComponent = withSessionCheck(PpMessageThreadPage, [
    "userSession",
  ]);
  const ContractorPermitsComponent = withSessionCheck(ContractorPermitsPage, [
    "userSession",
  ]);
  const ContractorPermitInspectionsComponent = withSessionCheck(
    ContractorPermitInspectionsPage,
    ["userSession"],
  );
  const ContractorUploadsComponent = withSessionCheck(ContractorUploadsPage, [
    "userSession",
  ]);
  const ContractorMessagingComponent = withSessionCheck(
    ContractorMessagingPage,
    ["userSession"],
  );
  const ContractorShellComponent = withSessionCheck(ContractorShell, [
    "userSession",
  ]);
  const PpEditInspectionsComponent = withSessionCheck(PpEditInspectionsPage, [
    "userSession",
  ]);
  const PpStampingWorkflowComponent = withSessionCheck(PpStampingWorkflowPage, [
    "userSession",
  ]);
  const PpDocumentPreviewComponent = withSessionCheck(PpDocumentPreviewPage, [
    "userSession",
  ]);
  const PpCityReportingComponent = withSessionCheck(PpCityReportingPage, [
    "userSession",
  ]);
  // Photo and Drawing Components
  const PpPropertyPhotoGalleryComponent = withSessionCheck(
    PpPropertyPhotoGalleryPage,
    ["userSession"],
  );
  const PpMobilePhotoCaptureComponentWrapper = withSessionCheck(
    PpMobilePhotoCaptureComponent,
    ["userSession"],
  );
  const PpPhotoAnnotationToolsComponent = withSessionCheck(
    PpPhotoAnnotationTools,
    ["userSession"],
  );
  const PpPhotoRequirementsSystemComponent = withSessionCheck(
    PpPhotoRequirementsSystem,
    ["userSession"],
  );
  const SubusersComponent = withSessionCheck(Subusers, [
    "userSession",
    "privateProviderSession",
  ]);

  //? PRIVATE PROVIDER ROUTES ///////////////////////////////////////////////////////////////////////////

  //? ////////////////////////////////////////////////////////////////////////////////////////

  //! ADMIN ONLY ROUTES ///////////////////////////////////////////////////////////////////////////
  const OverviewComponent = withSessionCheck(Overview, ["userSession"]);
  const CustomersComponent = withSessionCheck(Customers, ["userSession"]);
  const SalesComponent = withSessionCheck(Sales, ["userSession"]);
  const TasksComponent = withSessionCheck(Tasks, ["userSession"]);
  const LicensesComponent = withSessionCheck(Licenses, ["userSession"]);
  const ManagementComponent = withSessionCheck(Management, ["userSession"]);
  const NotariesComponent = withSessionCheck(Notaries, ["userSession"]);
  const SessionUsageComponent = withSessionCheck(SessionUsage, ["userSession"]);
  const ReportsComponent = withSessionCheck(Reports, ["userSession"]);
  const FormsManagementComponent = withSessionCheck(FormsManagement, [
    "userSession",
  ]);
  //! ////////////////////////////////////////////////////////////////////////////////////////
  // const NotaryQueueComponent = withSessionCheck(NotaryQueue, "notarySession");

  // ! SIGNEE ROUTES - GUEST SESSION /////////////////////////////////////////////////////////////////////
  const RegisterSigneeComponent = withSessionCheck(
    RegisterSignee,
    ["guestSession"],
    null,
    {},
    false,
  );
  const RegisterRecordSigneeComponent = withSessionCheck(
    RegisterRecordSignee,
    ["guestSession"],
    null,
    {},
    false,
  );
  const ManageSigneeAppointmentComponent = withSessionCheck(
    ManageSigneeAppointment,
    ["guestSession"],
    null,
    {},
    false,
  );
  //! /////////////////////////////////////////////////////////////////////////////////////////////////////
  // const showNavUserList = [
  //   1, 6, 13, 14, 15, 16, 17, 18, 19, 20, 22, 24, 26, 27, 28, 29,
  // ];
  const showNavUserList = [
    1, 6, 10, 11, 13, 14, 15, 16, 17, 18, 19, 20, 22, 24, 26, 27, 28, 29, 30,
  ];
  const showNavSubList = [1, 8, 9, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];

  const userLoadingBtn = [30];
  const subUserLoadingBtn = [20];

  const disableUserPrev = [0, 1, 30];
  const disableSubUserPrev = [0, 1, 20];

  const renderMainComponents = () =>
    tabletSize &&
    [
      "/",
      "/login",
      "/register",
      "/signee/auth-login",
      "/notary/signee/register",
      "/record/signee/register",
      "/signee/manage",
      "/resetpassword",
      "/2fa-secure",
      "/2fa-secure/email",
      "/2fa-secure/totp",
      "/2fa-secure/sms",
      "/2fa-secure/email/pass",
      "/ron/user/completed",
      // Notary Routes
      "/notary/dashboard",
      "/notary/history",
      "/notary/settings",
      "/notary/finalize",
      "/notary/accept-instant",
      // Sales Routes
      "/sales/register",
      "/sales/login",
      "/sales/dashboard",
      "/sales/revenue",
      "/sales/customers",
    ].includes(location.pathname) ? (
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="*" element={<NotFound />} />
        <Route path="/notFound" element={<NotFound />} />
        <Route path="/register" element={<Register />} />
        <Route
          path="/register/user/complete"
          element={<CompleteUserRegistration />}
        />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/s/:code" element={<ShortRedirect />} />

        <Route path="/signee/auth-login" element={<SigneeLogin />} />
        {/* notarySession */}
        <Route element={<NotaryProtectedLayoutComponent />}>
          <Route path="/notary/dashboard" element={<NotaryDashboard />} />
          <Route path="/notary/organization" element={<NotaryOrganization />} />
          <Route path="/notary/history" element={<NotaryJobManagement />} />
          <Route path="/notary/settings" element={<NotarySettings />} />
          <Route path="/notary/finalize" element={<FinishNotary />} />
        </Route>
        <Route path="/notary/register" element={<NotaryRegister />} />
        <Route path="/notary/accept-instant" element={<AcceptInstant />} />
        {/*  */}
        <Route
          path="/notary/signee/register"
          element={<RegisterSigneeComponent />}
        />
        <Route
          path="/record/signee/register"
          element={<RegisterRecordSigneeComponent />}
        />
        <Route
          path="/signee/manage"
          element={<ManageSigneeAppointmentComponent />}
        />
        <Route path="/resetpassword" element={<ResetPassword />} />
        <Route path="/2fa-secure" element={<DynamicAuth />} />
        <Route path="/2fa-secure/email" element={<EmailFaAuth />} />
        <Route path="/2fa-secure/totp" element={<TotpAuth />} />
        <Route path="/2fa-secure/sms" element={<SmsFaAuth />} />
        <Route path="2fa-secure/email/pass" element={<EmailFaAuthPass />} />
        <Route path="/ron/user/completed" element={<UserRonCompleted />} />

        {/* SALES */}
        <Route path="/sales/register" element={<SalesRegister />} />
        <Route path="/sales/login" element={<SalesLogin />} />
        <Route
          path="/sales"
          element={
            <SalesAuthWrapper>
              <SalesLayout />
            </SalesAuthWrapper>
          }
        >
          <Route path="dashboard" element={<SalesDashboard />} />
          <Route path="revenue" element={<SalesRevenue />} />
          <Route path="customers" element={<SalesCustomers />} />
        </Route>
      </Routes>
    ) : (
      // ) :
      // MOBILE UNSUPPORTED SCREEN (TEMP DISABLED)
      // To re-enable, restore the previous ternary branch:
      // tabletSize ? (
      //   <Box
      //     sx={{
      //       display: "flex",
      //       flexDirection: "column",
      //       justifyContent: "center",
      //       alignItems: "center",
      //       height: "100vh",
      //       textAlign: "center",
      //       padding: "20px",
      //     }}
      //   >
      //     <Typography variant="h4" component="h1">
      //       Screen Size Unsupported
      //     </Typography>
      //     <Typography variant="body1" sx={{ marginTop: 2 }}>
      //       Our application is currently being optimized for smaller screens.
      //       Please switch to a device with a larger display to continue, or check
      //       back later for mobile support.
      //     </Typography>
      //     {currentRole && (
      //       <Box sx={{ display: "flex", justifyContent: "center" }}>
      //         <LoadingButton
      //           onClick={async () => {
      //             if (sessions.currentSession) {
      //               await dispatch(disconnectWebsocket(sessions.currentSession));
      //             }
      //             sessionStorage.removeItem("__nocCreator__r_rid");
      //             sessionStorage.removeItem("exp");
      //             navigate("/login");
      //           }}
      //         >
      //           Log out
      //         </LoadingButton>
      //       </Box>
      //     )}
      //   </Box>
      // ) : (
      <Routes>
        <Route path="/" element={<Home />} />
        {/* userSession */}
        <Route path="/dashboard" element={<DashboardComponent />} />
        <Route path="/permits/create" element={<CreateNocComponent />} />
        <Route path="/building-permit" element={<BuildingPermitComponent />} />
        <Route path="/selectcounties" element={<SelectCountiesComponent />} />
        <Route path="/defaults" element={<PresetsComponent />} />
        <Route path="/permits/my-permits" element={<MyPermitsComponent />} />
        <Route
          path="/permits/subuser-permits"
          element={<SubuserPermitsComponent />}
        />
        <Route
          path="/permits/customers-packages"
          element={<CustomersPackagesComponent />}
        />
        <Route
          path="/permits/invitations"
          element={<NotarizationRequestsComponent />}
        />
        {/* UPLOADER */}
        <Route
          path="/custom-documents/sign"
          element={<CustomDocumentsPrepareSignComponent />}
        />
        <Route
          path="/custom-documents/noc"
          element={<CustomDocumentsPrepareNocComponent />}
        />
        <Route
          path="/custom-documents/prepared-docs"
          element={<CustomDocumentsPreparedDocsComponent />}
        />

        {/* notarySession */}
        <Route element={<NotaryProtectedLayoutComponent />}>
          <Route path="/notary/dashboard" element={<NotaryDashboard />} />
          <Route path="/notary/organization" element={<NotaryOrganization />} />
          {/* <Route path="/notary/queue" element={<NotaryQueueComponent />} /> */}
          <Route path="/notary/history" element={<NotaryJobManagement />} />
          <Route path="/notary/ipen" element={<NotaryIpenManagement />} />
          <Route path="/notary/settings" element={<NotarySettings />} />
          <Route path="/notary/finalize" element={<FinishNotary />} />
        </Route>

        {/* PRIVATE PROVIDER ROUTES */}

        {/*  */}
        <Route
          path="/privateProvider/contractors"
          element={<PrivateProviderContractorsComponent />}
        />

        {/* Private Provider Module Routes */}
        <Route path="/pp" element={<PpShellComponent />}>
          <Route index element={<PpDashboardComponent />} />
          <Route path="submissions" element={<PpSubmissionsListComponent />} />
          <Route
            path="submissions/new"
            element={<PpNewSubmissionComponent />}
          />
          <Route
            path="submissions/:id"
            element={<PpSubmissionDetailsComponent />}
          />
          <Route
            path="workbench/:id"
            element={<PpReviewWorkbenchComponent />}
          />
          <Route path="inspections" element={<PpInspectionsBoardComponent />} />
          <Route
            path="inspections/:permitId"
            element={<PpInspectionPermitViewComponent />}
          />
          <Route
            path="inspections/templates"
            element={<PpInspectionTemplatesComponent />}
          />
          <Route
            path="inspections/:permitId/edit"
            element={<PpEditInspectionsComponent />}
          />
          <Route
            path="submissions/:submissionId/stamp"
            element={<PpStampingWorkflowComponent />}
          />
          <Route
            path="submissions/:submissionId/preview"
            element={<PpDocumentPreviewComponent />}
          />
          <Route path="finance" element={<PpFinanceDashboardComponent />} />
          <Route path="finance/payouts" element={<PpPayoutsComponent />} />
          <Route
            path="finance/statements"
            element={<PpStatementsComponent />}
          />
          <Route path="messaging" element={<PpMessagingCenterComponent />} />
          <Route
            path="messaging/:threadId"
            element={<PpMessageThreadComponent />}
          />
          <Route
            path="notifications"
            element={<PpNotificationsCenterComponent />}
          />
          <Route path="settings" element={<PpSettingsComponent />} />
          <Route
            path="settings/city-reporting"
            element={<PpCityReportingComponent />}
          />
          <Route
            path="settings/digital-signature"
            element={<PpDigitalSignatureSetupComponent />}
          />
          {/* Photo and Drawing Features */}
          <Route path="photos" element={<PpPropertyPhotoGalleryComponent />} />
          <Route
            path="photos/capture"
            element={<PpMobilePhotoCaptureComponentWrapper />}
          />
          <Route
            path="photos/annotate/:photoId"
            element={<PpPhotoAnnotationToolsComponent />}
          />
          <Route
            path="photos/requirements"
            element={<PpPhotoRequirementsSystemComponent />}
          />
        </Route>

        {/* Contractor-facing routes */}
        <Route path="/pp/contractor" element={<ContractorShellComponent />}>
          <Route path="permits" element={<ContractorPermitsComponent />} />
          <Route
            path="permits/:id/inspections"
            element={<ContractorPermitInspectionsComponent />}
          />
          <Route path="uploads" element={<ContractorUploadsComponent />} />
          <Route path="messaging" element={<ContractorMessagingComponent />} />
        </Route>
        {/*  */}
        <Route path="/profilesettings" element={<ProfileSettingsComponent />} />
        <Route path="/contractors" element={<ContractorsComponent />} />
        <Route path="/branches" element={<BranchesComponent />} />
        <Route path="/payment" element={<PaymentComponent />} />
        <Route path="/subusers" element={<SubusersComponent />} />
        <Route path="/subscription" element={<SubscriptionComponent />} />
        {/* userSession -> ADMIN */}
        <Route path="/admin/notaries" element={<NotariesComponent />} />
        <Route path="/admin/overview" element={<OverviewComponent />} />
        <Route
          path="/admin/session-usage"
          element={<SessionUsageComponent />}
        />
        <Route path="/admin/customers" element={<CustomersComponent />} />
        <Route path="/admin/sales" element={<SalesComponent />} />
        <Route path="/admin/tasks" element={<TasksComponent />} />
        <Route path="/admin/licenses" element={<LicensesComponent />} />
        <Route path="/admin/management" element={<ManagementComponent />} />
        <Route
          path="/admin/reports"
          element={
            isPermitRockstarDomain() ? (
              <Navigate to="/admin/overview" replace />
            ) : (
              <ReportsComponent />
            )
          }
        />
        <Route
          path="/admin/forms-management"
          element={
            isPermitRockstarDomain() ? (
              <Navigate to="/admin/overview" replace />
            ) : (
              <FormsManagementComponent />
            )
          }
        />

        {/* ************************************************************************************ */}
        {/* guestSession */}
        <Route
          path="/notary/signee/register"
          element={<RegisterSigneeComponent />}
        />
        <Route
          path="/record/signee/register"
          element={<RegisterRecordSigneeComponent />}
        />
        <Route
          path="/signee/manage"
          element={<ManageSigneeAppointmentComponent />}
        />
        {/* ********************** */}
        {/* PRIVATE PROVIDER */}

        {/* ********************** */}

        {/* STRIPE */}
        <Route path="/changed-plan" element={<SuccessChangePlan />} />
        <Route
          path="/created-expired-plan"
          element={<SuccessCreateExpiredSubscription />}
        />
        {/* SUBUSER */}
        <Route path="/subuser/finalize" element={<SubuserFinalize />} />
        {/* 2FA */}
        <Route path="/2fa-secure" element={<DynamicAuth />} />
        <Route path="/2fa-secure/totp" element={<TotpAuth />} />
        <Route path="/2fa-secure/sms" element={<SmsFaAuth />} />
        <Route path="/2fa-secure/email" element={<EmailFaAuth />} />
        <Route path="2fa-secure/email/pass" element={<EmailFaAuthPass />} />
        {/* NOTARY */}
        <Route path="/notary/register" element={<NotaryRegister />} />
        <Route path="/notary/accept-instant" element={<AcceptInstant />} />
        {/* NOTARY */}

        <Route path="/2fa-secure/reset-pwd/totp" element={<TotpResetAuth />} />
        <Route path="/2fa-secure/reset-pwd/sms" element={<SmsFaResetAuth />} />
        <Route
          path="2fa-secure/reset-pwd/email"
          element={<EmailResetFaAuth />}
        />
        <Route
          path="2fa-secure/email/reset/pass"
          element={<EmailFaResetAuthPass />}
        />
        <Route path="/register" element={<Register />} />
        <Route
          path="/register/user/complete"
          element={<CompleteUserRegistration />}
        />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/s/:code" element={<ShortRedirect />} />
        <Route path="/signee/auth-login" element={<SigneeLogin />} />
        {/* RON COMPLETION PAGES */}
        <Route path="/ron/notary/completed" element={<NotaryRonCompleted />} />
        <Route path="/ron/user/completed" element={<UserRonCompleted />} />
        <Route path="/resetpassword" element={<ResetPassword />} />
        <Route path="/notFound" element={<NotFound />} />
        <Route path="*" element={<NotFound />} />

        {/* SALES */}
        <Route path="/sales/register" element={<SalesRegister />} />
        <Route path="/sales/login" element={<SalesLogin />} />
        <Route
          path="/sales"
          element={
            <SalesAuthWrapper>
              <SalesLayout />
            </SalesAuthWrapper>
          }
        >
          <Route path="dashboard" element={<SalesDashboard />} />
          <Route path="revenue" element={<SalesRevenue />} />
          <Route path="customers" element={<SalesCustomers />} />
        </Route>
      </Routes>
    );

  return (
    <StripeContext.Provider value={stripeRef}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {sessions?.currentSession ? (
          <TourProvider
            steps={
              sessions?.currentSession &&
              sessions?.currentSession === "subUserSession"
                ? subUserTourSteps
                : tourSteps
            }
            // startAt={5}
            disableKeyboardNavigation={["esc", "left", "right"]}
            showDots={false}
            showNavigation={true}
            badgeContent={({ totalSteps, currentStep }) =>
              currentStep + 1 + "/" + (totalSteps - 1)
            }
            nextButton={({
              currentStep,
              stepsLength,
              setIsOpen,
              setCurrentStep,
              steps,
            }) => {
              const last = currentStep === stepsLength - 2;
              const isUser = sessions?.currentSession === "userSession";
              const showNavList = isUser ? showNavUserList : showNavSubList;
              const isLoading =
                isUser && userLoadingBtn.includes(currentStep)
                  ? true
                  : !isUser && subUserLoadingBtn.includes(currentStep)
                    ? true
                    : false;
              return (
                <LoadingButton
                  color={
                    isUser && currentStep === 11
                      ? "success"
                      : !isUser && currentStep === 9
                        ? "success"
                        : "primary"
                  }
                  loading={isLoading}
                  variant="contained"
                  sx={{
                    display:
                      showTourNav || showNavList.includes(currentStep)
                        ? "flex"
                        : "none",
                  }}
                  onClick={() => {
                    if (isUser && currentStep === 11) {
                      setCurrentStep((s) => 30);
                    } else if (!isUser && currentStep === 9) {
                      setCurrentStep((s) => 20);
                    } else {
                      if (!showTourNav && !showNavList.includes(currentStep)) {
                        return;
                      } else if (last) {
                        setIsOpen(false);
                        navigate("/dashboard");
                      } else {
                        setCurrentStep((s) =>
                          s === steps?.length - 1 ? 0 : s + 1,
                        );
                      }
                    }
                  }}
                  endIcon={<ArrowForwardIcon />}
                >
                  {isUser && currentStep === 11
                    ? "Create Permit Package"
                    : !isUser && currentStep === 9
                      ? "Create Permit Package"
                      : last
                        ? "Finish"
                        : "Next"}
                </LoadingButton>
              );
            }}
            prevButton={({ currentStep, setCurrentStep }) => {
              const forUserPrevList = {
                1: "/dashboard",
                2: "/selectcounties",
                3: "/selectcounties",
                6: "/defaults",
                9: "/contractors",
                12: "/permits/create",
                22: "/permits/my-permits",
                24: "/subusers",
                26: "/subscription",
              };
              const forUserDoubleStepper = {
                9: 2,
                12: 3,
                3: 2,
              };
              const subUserPrevList = {
                1: "/dashboard",
                2: "/selectcounties",
                3: "/selectcounties",
                6: "/defaults",
                10: "/permits/create",
              };
              const subUserDoubleStepper = {
                10: 3,
              };
              const userPrevList = [
                "userSession",
                "privateProviderSession",
              ].includes(sessions?.currentSession)
                ? forUserPrevList
                : subUserPrevList;
              const userDoubleStepper = [
                "userSession",
                "privateProviderSession",
              ].includes(sessions?.currentSession)
                ? forUserDoubleStepper
                : subUserDoubleStepper;

              return (
                <IconButton
                  disabled={
                    (["userSession", "privateProviderSession"].includes(
                      sessions?.currentSession,
                    ) &&
                      disableUserPrev.includes(currentStep)) ||
                    (sessions?.currentSession === "subUserSession" &&
                      disableSubUserPrev.includes(currentStep))
                  }
                  sx={{
                    visibility:
                      (["userSession", "privateProviderSession"].includes(
                        sessions?.currentSession,
                      ) &&
                        disableUserPrev.includes(currentStep)) ||
                      (sessions?.currentSession === "subUserSession" &&
                        disableSubUserPrev.includes(currentStep))
                        ? "hidden"
                        : "visible",
                  }}
                  onClick={() => {
                    if (
                      currentStep in userPrevList &&
                      location.pathname !== userPrevList[currentStep]
                    ) {
                      navigate(userPrevList[currentStep]);
                      if (currentStep in userDoubleStepper) {
                        setTimeout(() => {
                          setCurrentStep(
                            (s) => s - userDoubleStepper[currentStep],
                          );
                        }, 200);
                      } else {
                        // if (skipSteps.includes(currentStep)) {
                        //   setTimeout(() => {
                        //     setCurrentStep((s) => s - 1);
                        //   }, 1000);
                        // } else {
                        setTimeout(() => {
                          setCurrentStep((s) => s - 1);
                        }, 200);
                        // }
                      }
                    } else {
                      if (currentStep in userDoubleStepper) {
                        setCurrentStep(
                          (s) => s - userDoubleStepper[currentStep],
                        );
                      } else {
                        setCurrentStep((s) => s - 1);
                      }
                    }
                  }}
                >
                  <ArrowBack />
                </IconButton>
              );
            }}
            onClickMask={({ setIsOpen }) => {}}
            styles={{
              popover: (base) => ({
                ...base,
                "--reactour-accent": "#ef5a3d",
                borderRadius: 20,
              }),
              badge: (base) => ({ ...base, left: "auto", right: "auto" }),
              maskArea: (base) => ({ ...base, rx: 20 }),
            }}
          >
            {/* Render your main components with TourProvider */}
            {renderMainComponents()}
          </TourProvider>
        ) : (
          // Render your main components without TourProvider while waiting
          renderMainComponents()
        )}
      </LocalizationProvider>
    </StripeContext.Provider>
  );
};

export default App;
